import { defineStore } from 'pinia'
import { getInviteInfo, getIpRegion, getUserInfo, logout, signInForFreeCredits } from '~/composables/api/user.js'
import { syncAuthSessionHintCookie } from '~/composables/useAuthSessionHint'
import { resolveDailyBonusFallback } from '~/constants/dailyBonus.js'
import { syncUserVideoTasks } from '~/utils/taskSync.js'

const DAILY_BONUS_STATUS = {
  SUCCESS: 'SUCCESS',
  PENDING: 'PENDING',
}

let freeCreditsClaimPromise = null
let inviteInfoRequest = null
let ipRegionRequest = null

const INVITE_INFO_CACHE_KEY = 'lip-free-credits-invite'

function normalizeInviteInfo(data = {}) {
  return {
    invite_link: String(data?.invite_link || '').trim(),
    invite_code: String(data?.invite_code || '').trim(),
  }
}

function readCachedInviteInfo(ownerKey) {
  if (!import.meta.client || !ownerKey) return null
  try {
    const cached = JSON.parse(localStorage.getItem(INVITE_INFO_CACHE_KEY) || 'null')
    if (cached?.owner_key !== ownerKey) return null
    const info = normalizeInviteInfo(cached)
    return info.invite_link && info.invite_code ? info : null
  } catch {
    return null
  }
}

function cacheInviteInfo(ownerKey, info) {
  if (!import.meta.client || !ownerKey) return
  try {
    localStorage.setItem(
      INVITE_INFO_CACHE_KEY,
      JSON.stringify({ owner_key: ownerKey, ...normalizeInviteInfo(info) }),
    )
  } catch {}
}

function buildInviteOwnerKey(userId, email) {
  const id = String(userId ?? '').trim()
  if (id) return `id:${id}`
  const normalizedEmail = String(email || '')
    .trim()
    .toLowerCase()
  return normalizedEmail ? `email:${normalizedEmail}` : ''
}

function firstDefined(values) {
  return values.find((v) => v !== undefined && v !== null && v !== '')
}

function normalizeDailyBonusStatus(value) {
  const status = String(value ?? '').trim().toUpperCase()
  if (status === DAILY_BONUS_STATUS.PENDING) return DAILY_BONUS_STATUS.PENDING
  if (status === DAILY_BONUS_STATUS.SUCCESS) return DAILY_BONUS_STATUS.SUCCESS
  return ''
}

function readDailyBonusStatus(data = {}) {
  const ui = data?.user_info || {}
  const status = normalizeDailyBonusStatus(
    firstDefined([
      data?.daily_bonus_status,
      data?.dailyBonusStatus,
      ui?.daily_bonus_status,
      ui?.dailyBonusStatus,
    ]),
  )
  if (status) return status
  if (data?.daily_grant === true || data?.daily_grant === false) {
    return DAILY_BONUS_STATUS.SUCCESS
  }
  return ''
}

function readDailyBonusAmount(data = {}, fallback = 0) {
  const ui = data?.user_info || {}
  const raw = firstDefined([
    data?.daily_credits_grant,
    data?.daily_bonus_amount,
    data?.free_credits_grant,
    data?.free_credits_add,
    data?.credits_add,
    data?.amount,
    ui?.daily_credits_grant,
    ui?.daily_bonus_amount,
  ])
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback
}

function normalizeCreditNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

/** 全站用户会话与登录相关 UI 状态（单一定义，避免与 `stores/` 下其它 demo 撞名） */
export const useUserStore = defineStore('user', {
  persist: {
    omit: [
      'login_modal_show',
      'price_modal_show',
      'check_modal_show',
      'daily_bonus_notice_nonce',
      'daily_bonus_notice_amount',
      'daily_bonus_notice_source',
      'daily_bonus_amount',
      'ip_country',
    ],
  },
  state: () => {
    return {
      token: '',
      userId: '',
      userName: '',
      /** 展示用昵称（如谷歌 name）；无则头像字母取自邮箱 */
      displayName: '',
      credits: 0,
      /** 与后端字段对齐；缺省时由 getUserInfo 从 total 推导展示 */
      free_credits: 0,
      paid_credits: 0,
      daily_bonus_status: DAILY_BONUS_STATUS.SUCCESS,
      daily_bonus_amount: 0,
      daily_bonus_notice_nonce: 0,
      daily_bonus_notice_amount: 0,
      daily_bonus_notice_source: '',
      login_modal_show: false,
      price_modal_show: false,
      kodepay_user_id: '',
      invite_link: '',
      invite_code: '',
      has_paid_credits: false,
      check_modal_show: false,
      /** 未登录访客标识，用于上传等接口 authorization */
      uuid: '',
      ip_country: '',
    }
  },
  getters: {
    getToken() {
      return this.token
    },
    /** 签到展示数量：后端已返回则用之，否则按 IP 国家 20/30 */
    displayDailyBonusAmount(state) {
      const n = Number(state.daily_bonus_amount)
      if (Number.isFinite(n) && n > 0) return Math.floor(n)
      return resolveDailyBonusFallback(state.ip_country)
    },
    /** 顶栏 / 侧栏展示总积分：优先 free+paid；二者皆空时回退 credits */
    totalCredits(state) {
      const free = normalizeCreditNumber(state.free_credits)
      const paid = normalizeCreditNumber(state.paid_credits)
      if (free > 0 || paid > 0) return free + paid
      return normalizeCreditNumber(state.credits)
    },
  },
  actions: {
    setToken(token) {
      this.token = token
      if (import.meta.client) {
        syncAuthSessionHintCookie(token)
      }
    },
    setUserName(userName) {
      this.userName = userName
    },
    setDisplayName(name) {
      this.displayName = name || ''
    },
    syncCreditsFromBreakdown() {
      this.credits =
        normalizeCreditNumber(this.free_credits) +
        normalizeCreditNumber(this.paid_credits)
    },
    setFreeCredits(n) {
      this.free_credits = Math.max(0, Number(n) || 0)
      this.syncCreditsFromBreakdown()
    },
    setPaidCredits(n) {
      this.paid_credits = Math.max(0, Number(n) || 0)
      this.syncCreditsFromBreakdown()
    },
    setCredits(credits) {
      this.credits = normalizeCreditNumber(credits)
    },
    setDailyBonusStatus(status) {
      this.daily_bonus_status =
        normalizeDailyBonusStatus(status) || DAILY_BONUS_STATUS.SUCCESS
    },
    setDailyBonusAmount(amount) {
      this.daily_bonus_amount = readDailyBonusAmount({ amount }, this.daily_bonus_amount)
    },
    notifyDailyBonusClaimed(amount, source = 'reward_daily_claim') {
      this.daily_bonus_notice_amount = readDailyBonusAmount(
        { amount },
        this.displayDailyBonusAmount,
      )
      this.daily_bonus_notice_source = String(source || 'reward_daily_claim')
      this.daily_bonus_notice_nonce += 1
    },
    setLoginModalShow(flag) {
      this.login_modal_show = flag
    },
    setPriceModalShow(flag) {
      this.price_modal_show = flag
    },
    setCheckModalShow(flag) {
      this.check_modal_show = flag
    },
    setKodepayUserId(id) {
      this.kodepay_user_id = id
    },
    setUserId(userId) {
      this.userId = userId == null ? '' : String(userId)
    },
    setInviteInfo(data = {}) {
      const info = normalizeInviteInfo(data)
      this.invite_link = info.invite_link
      this.invite_code = info.invite_code
    },
    setHasPaidCredits(flag) {
      this.has_paid_credits = flag
    },
    setUuid(uuid) {
      this.uuid = uuid || ''
    },
    async logout(router) {
      const { code, data, message } = await logout()
      this.setToken('')
      this.setUserId('')
      this.setCredits(0)
      this.setUserName('')
      this.setDisplayName('')
      this.setFreeCredits(0)
      this.setPaidCredits(0)
      this.setDailyBonusStatus(DAILY_BONUS_STATUS.SUCCESS)
      this.daily_bonus_amount = 0
      this.setKodepayUserId('')
      this.setInviteInfo()
      this.setUuid('')
      localStorage.setItem('user_id', '')
      localStorage.setItem('token', '')
      setTimeout(() => {
        location.reload()
      }, 1000)
    },
    applyCreditSnapshot(data = {}, options = {}) {
      const ui = data.user_info || {}
      const creditsRaw = firstDefined([data.credits, ui.credits])
      const freeRaw = firstDefined([
        data.free_credits,
        data.freeCredits,
        ui.free_credits,
        ui.freeCredits,
      ])
      const paidRaw = firstDefined([
        data.pay_credits,
        data.paid_credits,
        data.paidCredits,
        ui.pay_credits,
        ui.paid_credits,
        ui.paidCredits,
      ])

      const hasCredits = creditsRaw !== undefined && creditsRaw !== null && creditsRaw !== ''
      const hasFree = freeRaw !== undefined && freeRaw !== null && freeRaw !== ''
      const hasPaid = paidRaw !== undefined && paidRaw !== null && paidRaw !== ''

      if (hasFree && hasPaid) {
        this.setFreeCredits(freeRaw)
        this.setPaidCredits(paidRaw)
      } else if (hasFree) {
        this.setFreeCredits(freeRaw)
        if (hasCredits) {
          this.setPaidCredits(Math.max(0, normalizeCreditNumber(creditsRaw) - this.free_credits))
        }
      } else if (hasPaid) {
        this.setPaidCredits(paidRaw)
        if (hasCredits) {
          this.setFreeCredits(Math.max(0, normalizeCreditNumber(creditsRaw) - this.paid_credits))
        }
      } else if (hasCredits) {
        const credits = normalizeCreditNumber(creditsRaw)
        if (options.deriveFreeFromExistingPaid) {
          this.setFreeCredits(Math.max(0, credits - this.paid_credits))
        } else if (options.missingBreakdownAsPaid) {
          this.setFreeCredits(0)
          this.setPaidCredits(credits)
        } else {
          this.setCredits(credits)
          return true
        }
      } else {
        return false
      }

      this.syncCreditsFromBreakdown()
      return true
    },
    async ensureInviteInfo(ownerKey) {
      if (!ownerKey || !this.token) return null

      const cached = readCachedInviteInfo(ownerKey)
      if (cached) {
        this.setInviteInfo(cached)
        return cached
      }

      this.setInviteInfo()
      if (inviteInfoRequest?.ownerKey === ownerKey) {
        return inviteInfoRequest.promise
      }

      const request = {
        ownerKey,
        promise: null,
      }
      request.promise = (async () => {
        const { code, data = {}, message } = (await getInviteInfo()) || {}
        if (code !== 100000) {
          throw new Error(message || 'Failed to get invite information')
        }

        const info = normalizeInviteInfo(data)
        if (!info.invite_link || !info.invite_code) {
          throw new Error('Invite information is incomplete')
        }

        const currentOwnerKey = buildInviteOwnerKey(this.userId, this.userName)
        if (currentOwnerKey === ownerKey) {
          this.setInviteInfo(info)
          cacheInviteInfo(ownerKey, info)
        }
        return info
      })()
      inviteInfoRequest = request

      try {
        return await request.promise
      } finally {
        if (inviteInfoRequest === request) inviteInfoRequest = null
      }
    },
    async ensureCurrentUserInviteInfo() {
      const ownerKey = buildInviteOwnerKey(this.userId, this.userName)
      return this.ensureInviteInfo(ownerKey)
    },
    async ensureIpRegion() {
      if (this.ip_country) return this.ip_country
      if (ipRegionRequest) return ipRegionRequest

      ipRegionRequest = (async () => {
        try {
          const { code, data } = (await getIpRegion()) || {}
          if (code === 100000) {
            const country = String(data?.country || '').trim().toUpperCase()
            if (country) this.ip_country = country
          }
        } catch {
          // keep default 20 fallback
        }
        return this.ip_country
      })()

      return ipRegionRequest
    },
    async getUserInfo() {
      if (!this.token) return
      const { code, data, message } = await getUserInfo()
      if (code === 100000) {
        const ui = data.user_info || {}
        const token = data.token
        const user_id = ui.user_id ?? data.user_id
        const user_email = ui.user_email ?? data.user_email
        const display =
          ui.display_name ||
          ui.name ||
          ui.nickname ||
          ui.google_name ||
          data.display_name ||
          ''
        this.setToken(token)
        this.setUserId(user_id)
        this.setUserName(user_email || ui.user_email || '')
        this.setDisplayName(display)
        if (!this.applyCreditSnapshot(data, { missingBreakdownAsPaid: true })) {
          this.free_credits = 0
          this.paid_credits = 0
          this.credits = 0
        } else {
          this.syncCreditsFromBreakdown()
        }
        this.setDailyBonusStatus(readDailyBonusStatus(data))
        this.setDailyBonusAmount(readDailyBonusAmount(data, this.daily_bonus_amount))
        this.login_modal_show = false
        this.setKodepayUserId(data.kodepay_user_id)
        this.setHasPaidCredits(data.has_paid_credits)
        localStorage.setItem('user_id', user_id != null ? String(user_id) : '')
        localStorage.setItem('token', token)
        try {
          await this.ensureCurrentUserInviteInfo()
        } catch (error) {
          console.error('Get invite information failed:', error)
        }
        if (
          localStorage.getItem('showCheckInModal') !== 'closed' &&
          !this.has_paid_credits
        ) {
          setTimeout(() => {
            this.setCheckModalShow(true)
          }, 2000)
        }
        void syncUserVideoTasks().catch(() => {})
      } else {
        this.setToken('')
        this.setUserId('')
        this.setCredits(0)
        this.setUserName('')
        this.setDisplayName('')
        this.setFreeCredits(0)
        this.setPaidCredits(0)
        this.setDailyBonusStatus(DAILY_BONUS_STATUS.SUCCESS)
        this.daily_bonus_amount = 0
        this.setKodepayUserId('')
        this.setInviteInfo()
        localStorage.setItem('user_id', '')
        localStorage.setItem('token', '')
      }
    },
    async claimDailyFreeCredits({ source = 'reward_daily_claim' } = {}) {
      if (!this.token) return { claimed: false }
      if (this.daily_bonus_status !== DAILY_BONUS_STATUS.PENDING) {
        return { claimed: false }
      }
      if (freeCreditsClaimPromise) return freeCreditsClaimPromise

      freeCreditsClaimPromise = (async () => {
        const res = await signInForFreeCredits()
        const { code, data = {}, message } = res || {}
        if (code !== 100000) {
          return { claimed: false, code, message }
        }

        const amount = readDailyBonusAmount(data, this.displayDailyBonusAmount)
        const freeBefore = this.free_credits
        const paidBefore = this.paid_credits
        const totalBefore = freeBefore + paidBefore

        const appliedCredits = this.applyCreditSnapshot(data, {
          deriveFreeFromExistingPaid: true,
        })

        if (!appliedCredits) {
          this.setFreeCredits(freeBefore + amount)
        } else {
          // 响应若未带齐积分字段，保证签到额度立即加到免费积分上
          const totalAfter = this.free_credits + this.paid_credits
          if (totalAfter < totalBefore + amount) {
            this.setFreeCredits(this.free_credits + (totalBefore + amount - totalAfter))
          }
          if (this.paid_credits < paidBefore) {
            this.setPaidCredits(paidBefore)
          }
        }
        this.syncCreditsFromBreakdown()

        this.setDailyBonusStatus(DAILY_BONUS_STATUS.SUCCESS)
        this.setDailyBonusAmount(amount)
        if (data.has_paid_credits !== undefined) {
          this.setHasPaidCredits(data.has_paid_credits)
        }
        this.notifyDailyBonusClaimed(amount, source)
        // 短延迟再拉 info，避免到账前的旧数据覆盖本地刚加上的签到积分
        if (import.meta.client) {
          window.setTimeout(() => {
            void this.getUserInfo().catch(() => {})
          }, 800)
        }
        return { claimed: true, amount, data }
      })()

      try {
        return await freeCreditsClaimPromise
      } finally {
        freeCreditsClaimPromise = null
      }
    },
  },
})
